import { VesselDetails } from "../../../search-vessels/models/search.interfaces";
import { api, apiUrl } from "../../../shared/services/service";
import { VesselCardVesselOption } from "../models/vessel-card.models";
import axios from "axios";

// My Vessels includes IMOS assignments that Vessel Search deliberately excludes.
// Its endpoint uses fixed 1000-row pages and reports an empty page as 404.
const getAssignedVesselCardPreference = async (
  imo: string,
  userName: string,
  signal: AbortSignal,
): Promise<VesselDetails | null> => {
  for (let page = 1; !signal.aborted; page += 1) {
    let rows: VesselDetails[];

    try {
      const response = await api.get<{ data: VesselDetails[] }>(`${apiUrl}/userAssignedVessel`, {
        signal,
        params: {
          ops_coordinator: userName,
          showInactiveVessels: "true",
          showcompletedVoyages: "true",
          includeCount: "false",
          page,
        },
      });
      rows = response.data.data ?? [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      throw error;
    }

    const assigned = rows.find((vessel) => String(vessel.IMO_NUMBER) === imo);

    if (assigned) {
      // Home treats IMOS assignments as locked, even when the response omits the coordinator field.
      return assigned.FROM_IMOS === "Y" ? { ...assigned, OPS_COORDINATOR: userName } : assigned;
    }

    if (rows.length < 1000) {
      return null;
    }
  }

  return null;
};

// Uses the same assignment and vessel-code fields as the existing My Vessels controls.
// Vessel Search accepts names only; exact IMO matching keeps similarly named vessels separate.
export const getVesselCardPreferenceVessel = async (
  vessel: VesselCardVesselOption,
  userName: string,
  signal: AbortSignal,
): Promise<VesselDetails | null> => {
  const assigned = await getAssignedVesselCardPreference(vessel.imo, userName, signal);

  if (assigned) {
    return assigned;
  }

  for (let page = 1; !signal.aborted; page += 1) {
    const response = await api.get<{ data: VesselDetails[] }>(`${apiUrl}/searchVessel`, {
      signal,
      params: {
        vessel_name: vessel.vesselName,
        user_name: userName,
        showInactiveVessels: "true",
        includeCount: "false",
        page,
        pageSize: 50,
      },
    });
    const rows = response.data.data ?? [];
    const match = rows.find((row) => String(row.IMO_NUMBER) === vessel.imo);

    if (match) {
      return match;
    }

    if (rows.length < 50) {
      return null;
    }
  }

  return null;
};
